import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import {useOrgTypeLabel} from 'modules/local/useOrgTypeLabels'
import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {Empty, Spin, Tree} from 'antd'
import {BsPeopleFill} from 'react-icons/bs'
import {MdOutlineDragIndicator} from 'react-icons/md'
import {formatNumber} from '../../../helpers/number'
import {organization_getGroupTree_Api, organization_updateGroup_Api} from '../../../apis'
import useAsyncAction from '../../../modules/asyncCache/useAsyncAction'
import {notifyOnError} from '../../AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify} from '../../AppLocals/factory/createNotification'
import WorkspaceContext from '../WorkspaceContext'

// ─── Build flat list → grouped by group_title ─────────────────────
const buildTree = (items) => {
    const groupMap = {}
    const ungrouped = []

    items.forEach(item => {
        const node = {
            key: item.id,
            _memberCount: item.member_count,
            _groupTitle: item.group_title,
            _title: item.title,
            _edit: item.edit,
            _isGroup: false,
            _isRoot: false,
            children: [],
        }
        const groupTitle = item.group_title
        if (groupTitle) {
            if (!groupMap[groupTitle]) {
                groupMap[groupTitle] = {
                    key: `__group__${groupTitle}`,
                    _title: groupTitle,
                    _isGroup: true,
                    _isRoot: false,
                    _memberCount: null,
                    _edit: false,
                    children: [],
                }
            }
            groupMap[groupTitle].children.push(node)
        } else {
            ungrouped.push(node)
        }
    })

    return [...Object.values(groupMap), ...ungrouped]
}

// ─── Tree node title ──────────────────────────────────────────────────────────
const NodeTitle = ({nodeData}) => {
    if (nodeData._isRoot) {
        return (
            <span className="flex items-center gap-2 py-0.5 select-none">
                <span className="font-bold text-sm text-color-000">{nodeData._title}</span>
            </span>
        )
    }

    if (nodeData._isGroup) {
        return (
            <span className="flex items-center gap-2 py-0.5 select-none">
                <span className="font-semibold text-xs text-indigo-500 uppercase tracking-wider">
                    {nodeData._title}
                </span>
            </span>
        )
    }

    return (
        <span className="flex items-center gap-2 py-0.5 min-w-0 select-none">
            <MdOutlineDragIndicator size={14} className="text-color-300 flex-shrink-0 cursor-grab"/>
            <span className="text-sm text-color-000 truncate">
                {nodeData._title}
            </span>
            <span className="ml-auto flex-shrink-0 flex items-center gap-1 text-xs text-color-400 pl-3">
                <BsPeopleFill size={10}/>
                {formatNumber(nodeData._memberCount ?? 0)}
            </span>
        </span>
    )
}

// ─── Augment tree nodes with rendered titles ──────────────────────────────────
const augmentNodes = (nodes) =>
    nodes.map(node => ({
        ...node,
        title: <NodeTitle nodeData={node}/>,
        icon: false,
        children: node.children?.length ? augmentNodes(node.children) : [],
    }))

// ─── Main component ───────────────────────────────────────────────────────────
const WorkspaceOrganizationGroupTree = () => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext)
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(false)

    const deptLabel = useOrgTypeLabel(
        organization?.org_type,
        'group',
        organization?.label_privacy
    ) || t('groups / ministries')

    const onError = notifyOnError(t)

    const {handleAsyncAction: fetchTree} = useAsyncAction({
        apiInfo: organization_getGroupTree_Api,
        onError,
        onSuccess: (__, data) => {
            const list = _.get(data, 'response.data') || []
            setItems(list)
            setLoading(false)
        },
    })

    const {handleAsyncAction: onUpdate} = useAsyncAction({
        apiInfo: organization_updateGroup_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
        },
    })

    useEffect(() => {
        if (!organization?.id) return
        setLoading(true)
        fetchTree({}, {id: organization.id})
    }, [organization?.id])

    // ─── Build tree data grouped by group_title ───────────────────
    const treeData = useMemo(() => {
        if (!items.length || !organization) return []
        const children = buildTree(items)
        return augmentNodes([{
            key: '__org_root__',
            _title: organization.title,
            _memberCount: null,
            _isRoot: true,
            _isGroup: false,
            _edit: false,
            children,
        }])
    }, [items, organization])

    // ─── Expand org root + all group nodes by default ────────────────────────
    const allKeys = useMemo(() => {
        const groupKeys = _.uniq(
            items
                .filter(i => i.group_title)
                .map(i => `__group__${i.group_title}`)
        )
        return ['__org_root__', ...groupKeys]
    }, [items])

    // ─── Drag-and-drop: update group_title ────────────────────────
    const handleDrop = useCallback((info) => {
        const {dragNode, node, dropToGap} = info
        const dragId = dragNode.key

        // Only dept nodes can be dragged
        if (dragNode._isGroup || dragNode._isRoot) return

        let newGroupTitle

        if (!dropToGap) {
            // Dropped INSIDE a node
            if (node.key === '__org_root__' || node._isRoot) {
                newGroupTitle = null
            } else if (node._isGroup) {
                newGroupTitle = node._title
            } else {
                // Dropped inside a dept — blocked by allowDrop
                return
            }
        } else {
            // Dropped in gap before/after a node → become sibling, inherit group
            if (node._isRoot || node.key === '__org_root__') {
                newGroupTitle = null
            } else if (node._isGroup) {
                // Gap adjacent to a group header → no group (root level)
                newGroupTitle = null
            } else {
                // Gap adjacent to a dept → inherit that dept's group
                newGroupTitle = node._groupTitle || null
            }
        }

        // Optimistic local update
        setItems(prev =>
            prev.map(item =>
                item.id === dragId
                    ? {...item, group_title: newGroupTitle}
                    : item
            )
        )

        onUpdate({group_title: newGroupTitle}, {id: dragId})
    }, [items, onUpdate])

    if (loading) {
        return (
            <div className="flex justify-center items-center py-16">
                <Spin size="large"/>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="flex flex-col gap-1">
                <h2 className="font-bold text-xl text-color-000 tracking-wide">
                    {t('org tree')}
                </h2>
                <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                <p className="text-color-300 tracking-wider">
                    {t(deptLabel)}
                </p>
            </div>

            {/* Hint */}
            <div className="px-3 py-2 rounded-lg bg-blue-50 border border-blue-400 text-blue-500 italic tracking-wider">
                <label className="font-medium text-blue-600">
                    {`* ${t('hint')}: `}
                </label>
                {t('drag departments to change their group')}
            </div>

            {/* Tree */}
            {!items.length ? (
                <Empty
                    className="py-10"
                    description={
                        <span className="text-color-300">{t('no data is available now')}</span>
                    }
                />
            ) : (
                <div className="p-3 border border-color-50 shadow-md background rounded-xl overflow-hidden">
                    <Tree
                        draggable={{icon: false}}
                        blockNode
                        showIcon={false}
                        defaultExpandedKeys={allKeys}
                        treeData={treeData}
                        onDrop={handleDrop}
                        allowDrop={({dropNode, dropPosition}) => {
                            // Can't drop before org root
                            if (dropNode.key === '__org_root__' && dropPosition === -1) return false
                            // Can't drop INSIDE a dept node (would create dept-in-dept)
                            if (!dropNode._isGroup && !dropNode._isRoot && dropPosition === 0) return false
                            return true
                        }}
                        className="org-dept-tree"
                    />
                </div>
            )}

            <div className="h-10"/>
        </div>
    )
}

export default WorkspaceOrganizationGroupTree
